import { ModuleContent } from '../types';
import { PdfRect, PdfUnit } from '../../pdf/pdf';

export type AnnotationType = 'Note' | 'Warning' | 'Error' | 'Good';

export interface Annotation {
  type: AnnotationType;
  text: string;
  controlRect?: PdfRect;
}

export interface UIControl {
  type: string;
  content?: string;
  attrs: Record<string, string>;
  children?: UIControl[];
  annotations: Annotation[];
}

export interface UIModuleContent extends ModuleContent {
  controls: UIControl[];
  annotations: Annotation[];
  notesPanelWidth: number;
}

const ANNOTATION_ATTRS: AnnotationType[] = ['Note', 'Warning', 'Error', 'Good'];

const CONTAINER_TYPES = [
  'StackPanel',
  'WrapPanel',
  'DockPanel',
  'Grid',
  'Border',
  'Menu',
  'GroupBox',
  'TabControl',
  'TabItem',
  'RowDefinition',
  'ColumnDefinition',
];

export function parseXAML(source: string): { controls: UIControl[]; annotations: Annotation[] } {
  const annotations: Annotation[] = [];
  const controls = parseElements(source, annotations);
  return { controls, annotations };
}

function parseElements(source: string, annotations: Annotation[]): UIControl[] {
  const elements: UIControl[] = [];
  let remaining = source.trim();

  while (remaining.length > 0) {
    const result = parseElement(remaining, annotations);
    if (result) {
      elements.push(result.element);
      remaining = result.remaining;
    } else {
      break;
    }
  }

  return elements;
}

function findCloseTag(source: string, tag: string): number {
  const open = `<${tag}`;
  const close = `</${tag}>`;
  let depth = 0;
  let i = 0;
  while (i < source.length) {
    if (source.startsWith(close, i)) {
      if (depth === 0) return i;
      depth--;
      i += close.length;
      continue;
    }
    if (source.startsWith(open, i)) {
      const after = source[i + open.length];
      if (
        !after ||
        after === ' ' ||
        after === '\t' ||
        after === '\n' ||
        after === '\r' ||
        after === '>' ||
        after === '/'
      ) {
        depth++;
      }
    }
    i++;
  }
  return -1;
}

function parseElement(
  source: string,
  annotations: Annotation[]
): { element: UIControl; remaining: string } | null {
  const openMatch = /^<([\w.]+)\s*([^>]*?)(\/?)>(\s*)/.exec(source);
  if (!openMatch) return null;

  const tagName = openMatch[1];
  const attrString = openMatch[2];
  const isSelfClosing = openMatch[3] === '/';
  const afterTag = source.slice(openMatch[0].length);

  const attrs = parseAttributes(attrString);
  const elementAnnotations: Annotation[] = [];

  for (const annType of ANNOTATION_ATTRS) {
    if (attrs[annType]) {
      elementAnnotations.push({ type: annType, text: attrs[annType] });
      annotations.push(elementAnnotations[elementAnnotations.length - 1]);
      delete attrs[annType];
    }
  }

  let content = '';
  let remaining = afterTag;
  let children: UIControl[] | undefined;

  if (!isSelfClosing) {
    if (CONTAINER_TYPES.includes(tagName) || tagName.includes('.')) {
      const childrenResult = parseContainerContent(tagName, remaining, annotations);
      if (childrenResult) {
        children = childrenResult.children;
        remaining = childrenResult.remaining;
      }
    } else {
      const contentMatch = /^([^<]*)/.exec(remaining);
      if (contentMatch) {
        content = contentMatch[1].trim();
        remaining = remaining.slice(contentMatch[0].length);
      }

      const closeMatch = new RegExp(`</${tagName}>`).exec(remaining);
      if (closeMatch) {
        remaining = remaining.slice(closeMatch[0].length);
      }
    }
  }

  const element: UIControl = {
    type: tagName,
    attrs,
    annotations: elementAnnotations,
  };

  if (content) {
    element.content = content;
  }

  if (children) {
    element.children = children;
  }

  return { element, remaining: remaining.trim() };
}

function parseContainerContent(
  containerType: string,
  source: string,
  annotations: Annotation[]
): { children: UIControl[]; remaining: string } | null {
  let remaining = source;

  const closeTag = `</${containerType}>`;
  const closeIndex = findCloseTag(remaining, containerType);

  if (closeIndex === -1) {
    return { children: [], remaining };
  }

  const content = remaining.slice(0, closeIndex);
  remaining = remaining.slice(closeIndex + closeTag.length);

  const rawChildren = parseElements(content, annotations);

  // Flatten property-element children (e.g. Grid.RowDefinitions → RowDefinition[])
  const children: UIControl[] = [];
  for (const child of rawChildren) {
    if (child.type.includes('.')) {
      children.push(...(child.children || []));
    } else {
      children.push(child);
    }
  }

  return { children, remaining };
}

function parseAttributes(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};

  const attrRegex = /([\w.]+)(?:="([^"]*)")?/g;
  let match;

  while ((match = attrRegex.exec(attrString)) !== null) {
    const name = match[1];
    const value = match[2] !== undefined ? match[2] : 'true';
    attrs[name] = value;
  }

  return attrs;
}
